import type { GetProfileOutput } from "#shared/directoryContract.ts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { createQueryOptions } from "@packages/core-client/api/createQueryOptions.ts";
import { retryUnlessNotFound } from "@packages/core-client/query/retryUnlessNotFound.ts";
import { useLoadingIndicator } from "@packages/core-client/hooks/useLoadingIndicator.tsx";
import { RoutedBreadcrumb } from "@packages/core-client/routing/RoutedBreadcrumb.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMatches } from "react-router";
import { useDirectoryClient } from "../../../api/queries/directory.ts";
import { i18NextInstanceKey } from "../../../i18n/i18next.ts";
import { ProfileHeader } from "./ProfileHeader.tsx";

const PLACEHOLDER_PROFILE: GetProfileOutput = {
    person: {
        id: "skeleton",
        displayName: "Xxxxxxx Xxxxxxxx",
        pronouns: "xxx/xxx",
        jobTitle: "Xxxxxxx Xxxxxxxx Xxxxxxx",
        departmentId: "skeleton-dept",
        departmentLabel: "Xxxxxxxxxx",
        workLocationId: "skeleton-loc",
        workLocationLabel: "Xxxxxxx, XX",
        hireDate: "2020-01-01T00:00:00.000Z",
        isNewHire: false,
        timeZone: undefined,
        email: "xxxxxxx@xxxxxxxx.xxx",
        bio: "Xxxxx xxxx xxxxxx xxxxxxx xxxx xxxxxxxxx xxxx xxxxxx xxxxxxxxx xxxxxx xxxx xxxxx xxxxxxx.",
        interests: ["Xxxxxxx", "Xxxx xxxxxxx", "Xxxxxxxx"],
        competencies: ["Xxxxxxxxxx", "Xxxxxxx xxxxxxx", "Xxxxxx"],
        manager: { id: "skeleton-mgr", displayName: "Xxxxx Xxxxx" }
    },
    directReports: [],
    inCommon: { location: "", department: "", competencies: [], interests: [] },
    isSelf: false,
    canViewFeedback: false,
    canViewReviews: false
};

export function ProfileTabsHeading() {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ProfileTabsHeading", { i18n: i18nextInstance });

    const matches = useMatches();
    const personId = matches[matches.length - 1]?.params?.personId ?? "";

    const { queryUtils } = useDirectoryClient();

    const { data, isPending } = useQuery(
        createQueryOptions(queryUtils.getProfile.queryOptions({ input: { personId } }), {
            throwOnError: false,
            retry: retryUnlessNotFound
        })
    );

    const isLoading = useLoadingIndicator(isPending);
    const hasContent = isLoading || Boolean(data);
    const profile = isLoading ? PLACEHOLDER_PROFILE : (data ?? PLACEHOLDER_PROFILE);

    const { person } = profile;

    return (
        <div className="w-full">
            <output className="sr-only">{isLoading && t("liveRegion.loading")}</output>

            {hasContent && (
                <Skeleton isLoading={isLoading} inert={isLoading}>
                    <div className="flex flex-col gap-6">
                        <RoutedBreadcrumb ariaLabel={t("breadcrumb.ariaLabel")} items={[{ label: t("breadcrumb.people"), href: "/org/employees" }, { label: person.displayName }]} />

                        <ProfileHeader person={person} />
                    </div>
                </Skeleton>
            )}
        </div>
    );
}
